import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { CHANGE_STATE_COLORS, OUTSTANDING_STATES, STATE_LABELS } from './constants';

/** the ring's geometry, in the SVG's own units */
const SIZE = 64;
const STROKE = 8;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** surface showing between two segments, so nothing needs a border to separate it */
const GAP = 2.2;

/**
 * How much of an application's outstanding work sits in each state.
 *
 * Part-to-whole at a glance over four segments — which is the one job a ring
 * is good at. It is the only place the card says anything about change
 * instances, so it carries the numbers itself: the total in the middle, and
 * each segment's state and count on its own hover and in the label the ring
 * gives assistive tech.
 *
 * Nothing outstanding is not an empty chart — it is the answer "there is
 * nothing left". So the ring closes in the completed colour and is pushed out
 * of focus, with a tick in the middle saying so in a way that does not depend
 * on seeing it.
 */
const ChangeDonut = ({ counts }: { counts: Record<string, number> }) => {
    const segments = OUTSTANDING_STATES
        .map((state) => ({ state, value: counts[state] ?? 0 }))
        .filter((segment) => segment.value > 0);

    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    const settled = total === 0;

    // one segment is a whole ring, and a ring has no neighbour to be parted from
    const gap = segments.length > 1 ? GAP : 0;

    let travelled = 0;

    return (
        <div className={`app-donut${settled ? ' app-donut--settled' : ''}`}>
            <svg
                className="app-donut__ring"
                width={SIZE}
                height={SIZE}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                role={settled ? 'presentation' : 'img'}
                aria-hidden={settled || undefined}
                aria-label={
                    settled
                        ? undefined
                        : `Outstanding changes: ${segments
                              .map((segment) => `${segment.value} ${STATE_LABELS[segment.state].toLowerCase()}`)
                              .join(', ')}`
                }
            >
                {/* the track the segments sit on, so a part-full ring still reads
                    as a proportion of something rather than a stray arc */}
                <circle
                    className="app-donut__track"
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RADIUS}
                    fill="none"
                    strokeWidth={STROKE}
                />

                {settled ? (
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={CHANGE_STATE_COLORS.COMPLETED}
                        strokeWidth={STROKE}
                    />
                ) : (
                    segments.map((segment) => {
                        const length = (segment.value / total) * CIRCUMFERENCE;
                        // never shorten a slice out of existence: a count of one
                        // among many is still a fact the ring has to show
                        const drawn = Math.max(length - gap, 1.5);
                        const offset = travelled;
                        travelled += length;

                        return (
                            <circle
                                key={segment.state}
                                cx={SIZE / 2}
                                cy={SIZE / 2}
                                r={RADIUS}
                                fill="none"
                                stroke={CHANGE_STATE_COLORS[segment.state]}
                                strokeWidth={STROKE}
                                strokeDasharray={`${drawn} ${CIRCUMFERENCE - drawn}`}
                                strokeDashoffset={-offset}
                            >
                                <title>{`${STATE_LABELS[segment.state]}: ${segment.value}`}</title>
                            </circle>
                        );
                    })
                )}
            </svg>

            {/* outside the ring's own element, so it stays sharp while the
                settled ring behind it is pushed back */}
            <div className="app-donut__centre">
                {settled ? (
                    <CheckOutlined className="app-donut__tick" aria-hidden="true" />
                ) : (
                    <>
                        <span className="app-donut__total">{total}</span>
                        <span className="app-donut__unit">open</span>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChangeDonut;

/* No legend. The ring is the whole of what the card says about change
   instances, which is why its colours were re-stepped to stand on their own —
   see the note in constants.ts. */
